import React, { useRef, useState } from 'react';
import { useTranslate } from '../TranslationText';

/**
 * Clean file upload similar to FormFileUpload but WITHOUT react-hook-form dependency.
 * Designed to be used with standard React state (useState).
 */
export default function StandaloneFileUpload({
    name,
    label,
    required = false,
    disabled = false,
    accept = 'image/*,application/pdf',
    isMulti = false,
    maxSizeMB = 10,
    hideLabel = false,
    initialFiles = [],
    onChange,
}) {
    const { translate } = useTranslate();
    const inputRef = useRef(null);
    const [error, setError] = useState(null);

    const handleFileSelect = (e) => {
        if (!e.target.files || e.target.files.length === 0) return;

        const maxBytes = maxSizeMB * 1024 * 1024;
        const newFiles = [];
        let hasError = false;

        for (let i = 0; i < e.target.files.length; i++) {
            const file = e.target.files[i];

            if (file.size > maxBytes) {
                setError(translate('web.fileSizeLimit', 'File size 10MB limit.'));
                hasError = true;
                continue;
            }

            const allowedMimeTypes = ['image/', 'application/pdf'];
            if (!allowedMimeTypes.some((type) => file.type.startsWith(type))) {
                setError(translate('web.invalidFileType', 'Only images and PDFs allowed.'));
                hasError = true;
                continue;
            }

            newFiles.push({
                file,
                name: file.name,
                preview: URL.createObjectURL(file),
            });
        }

        if (!hasError) setError(null);

        if (newFiles.length > 0) {
            const result = isMulti ? [...(initialFiles || []), ...newFiles] : newFiles;
            if (onChange) onChange(result);
        }

        // Reset input so same file can be selected again
        if (inputRef.current) inputRef.current.value = '';
    };

    const removeFile = (index) => {
        const updated = [...(initialFiles || [])];
        if (updated[index]?.preview) URL.revokeObjectURL(updated[index].preview);
        updated.splice(index, 1);
        if (onChange) onChange(updated);
    };

    const openFile = (file) => {
        if (file.preview) {
            window.open(file.preview, '_blank', 'noopener,noreferrer');
        } else if (file.url) {
            window.open(file.url, '_blank', 'noopener,noreferrer');
        }
    };

    const files = initialFiles || [];
    const displayName = Array.isArray(files)
        ? files.map((f) => f.name || f.file?.name).join(', ')
        : '';

    return (
        <div className="mb-2 w-100">
            {!hideLabel && label && (
                <label htmlFor={name} className="capatalize_text">
                    {label}{' '}
                    {required && <i style={{ color: 'red', margin: 'auto 2px' }}>*</i>}
                </label>
            )}

            <div className="input-group row mx-0 rtl">
                <input
                    type="text"
                    className="form-control col-lg-7 col-md-12 col-sm-12 col-12"
                    placeholder={translate('web.noFileSelected', 'No file selected')}
                    style={{ cursor: 'pointer', fontSize: 13 }}
                    value={displayName}
                    readOnly
                    onClick={() => {
                        if (files.length === 1) openFile(files[0]);
                    }}
                />
                <div
                    className="input-group-text btn-dark text-center btn col-lg-5 col-md-12 col-sm-12 col-12"
                    style={{
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.65 : 1,
                        fontSize: 13,
                        zIndex: 1,
                    }}
                    onClick={() => {
                        if (!disabled && inputRef.current) inputRef.current.click();
                    }}
                >
                    {translate('web.uploadFile', 'Upload File')}
                </div>
            </div>

            <input
                ref={inputRef}
                type="file"
                multiple={isMulti}
                accept={accept}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
            />

            {/* File previews */}
            {Array.isArray(files) && files.length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {files.map((f, idx) => (
                        <div
                            key={idx}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                padding: '2px 8px',
                                background: '#f0f0f0',
                                borderRadius: 4,
                                fontSize: 12,
                            }}
                        >
                            <span
                                style={{ cursor: 'pointer', textDecoration: 'underline', color: '#1B6CB5' }}
                                onClick={() => openFile(f)}
                            >
                                {f.name || f.file?.name}
                            </span>
                            {!disabled && (
                                <span
                                    style={{ cursor: 'pointer', color: 'red', fontWeight: 'bold', marginLeft: 4 }}
                                    onClick={() => removeFile(idx)}
                                    title={translate('web.remove', 'Remove')}
                                >
                                    ×
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {error && (
                <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                    {error}
                </div>
            )}
        </div>
    );
}
